import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import { isNullOrUndefined } from 'utils/Global';

export type systemTaskState = {
  backgroundJobs: BackgroundJobDTO[];
};

const initialState = { backgroundJobs: [] } as systemTaskState;

const isSameBackgroundJob = (
  left: BackgroundJobDTO,
  jobId: BackgroundJobDTO['jobId'],
  jobType?: number,
) =>
  String(left.jobId) === String(jobId) &&
  (jobType === undefined || left.jobType === jobType);

const backgroundJobSlice = createSlice({
  name: 'backgroundJobs',
  initialState,
  reducers: {
    addBackgroundJob: (state, action: PayloadAction<BackgroundJobDTO>): void => {
      const existingIndex = state.backgroundJobs.findIndex((job) =>
        isSameBackgroundJob(job, action.payload.jobId, action.payload.jobType),
      );

      if (existingIndex >= 0) {
        state.backgroundJobs[existingIndex] = {
          ...state.backgroundJobs[existingIndex],
          ...action.payload,
        };
        return;
      }

      state.backgroundJobs.push(action.payload);
    },
    upsertBackgroundJob: (state, action: PayloadAction<BackgroundJobDTO>): void => {
      const existingJob = state.backgroundJobs.find(
        (job) =>
          job.jobId == action.payload.jobId &&
          Number(job.jobType) === Number(action.payload.jobType),
      );

      if (existingJob) {
        existingJob.name = action.payload.name;
        existingJob.progress = action.payload.progress;
        existingJob.logs = action.payload.logs;
        existingJob.dataObj = action.payload.dataObj;
        existingJob.jobStatus = action.payload.jobStatus;
      } else {
        state.backgroundJobs.push(action.payload);
      }
    },
    setBackgroundJobs: (state, action: PayloadAction<BackgroundJobDTO[]>): void => {
      state.backgroundJobs = action.payload;
    },
    removeBackGroundJob: (
      state,
      action: PayloadAction<{ id: number | string; type: number }>,
    ): void => {
      state.backgroundJobs = state.backgroundJobs.filter(
        (t) =>
          !(
            String(t.jobId) === String(action.payload.id) &&
            t.jobType === action.payload.type
          ),
      );
    },
    updateBackgroundJobProgress: (
      state,
      action: PayloadAction<{
        jobId: number | string;
        jobType?: number;
        progress?: number;
        name?: string;
        jobStatus?: number;
        dataObj?: any;
        logs?: any[];
      }>,
    ): void => {
      const job = state.backgroundJobs.find((t) =>
        isSameBackgroundJob(t, action.payload.jobId, action.payload.jobType),
      );
      if (!isNullOrUndefined(job)) {
        if (action.payload.name) {
          job.name = action.payload.name;
        }
        if (action.payload.jobStatus !== undefined) {
          job.jobStatus = action.payload.jobStatus;
        }
        if (action.payload.dataObj !== undefined) {
          job.dataObj = action.payload.dataObj;
        }
        if (action.payload.logs) {
          job.logs = action.payload.logs;
        }
        if (action.payload.progress !== undefined) {
          if (action.payload.progress === 100) {
            state.backgroundJobs = state.backgroundJobs.filter(
              (j) =>
                !isSameBackgroundJob(j, action.payload.jobId, action.payload.jobType),
            );
          } else {
            job.progress = Math.ceil(action.payload.progress);
          }
        }
      }
    },
    removeCompletedBackgroundJob: (state): void => {
      state.backgroundJobs = state.backgroundJobs.filter((t) => t.progress != 100);
    },
    removeAllBackgroundJobs: (state): void => {
      state.backgroundJobs = [];
    },
  },
});

export const {
  addBackgroundJob,
  upsertBackgroundJob,
  removeBackGroundJob,
  updateBackgroundJobProgress,
  removeCompletedBackgroundJob,
  setBackgroundJobs,
  removeAllBackgroundJobs,
} = backgroundJobSlice.actions;

export default backgroundJobSlice.reducer;
